import { useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import classnames from "classnames"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap"

import Breadcrumbs from "components/Common/Breadcrumb"
import DataCard from "components/Common/DataCard"
import SectionHeader from "components/Common/SectionHeader"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useAuth } from "hooks/authHook"
import { useUser } from "hooks/userHook"
import useUrlParams from "hooks/useUrlParams"
import { formatReadableDate } from "utils/formatDate"
import UserProfileCard from "./UserProfileCard"
import ActivityList from "./Activity/ActivityList"

const USER_PROFILE_BASE_TABS = ["account", "roles"]

const groupPermissionsByModule = permissions => {
  const groups = {}
  ;(permissions || []).forEach(permission => {
    const moduleName = permission?.module?.name || "Other"
    if (!groups[moduleName]) {
      groups[moduleName] = []
    }
    groups[moduleName].push(permission)
  })
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
}

const RoleCard = ({ role }) => {
  const [open, setOpen] = useState(false)
  const permissions = role?.permissions || []
  const groupedPermissions = useMemo(
    () => groupPermissionsByModule(permissions),
    [permissions]
  )

  return (
    <Card className="border shadow-none mb-0">
      <CardBody className="p-3">
        <div className="d-flex align-items-center justify-content-between gap-2">
          <div className="d-flex align-items-center gap-2 min-width-0">
            <i className="bx bx-shield-quarter text-primary font-size-18" />
            <h6 className="mb-0 fw-semibold text-truncate">{role.name}</h6>
            {role.is_system ? (
              <Badge className="badge-soft-dark border-0">System</Badge>
            ) : null}
          </div>
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <Badge className="badge-soft-primary border-0">
              {permissions.length} permissions
            </Badge>
            {permissions.length ? (
              <Button
                color="light"
                size="sm"
                onClick={() => setOpen(prev => !prev)}
              >
                <i className={`bx ${open ? "bx-chevron-up" : "bx-chevron-down"}`} />
              </Button>
            ) : null}
          </div>
        </div>

        {permissions.length ? (
          <Collapse isOpen={open} className="mt-3">
            <div className="d-flex flex-column gap-3">
              {groupedPermissions.map(([moduleName, modulePermissions]) => (
                <div key={moduleName}>
                  <div className="text-uppercase text-muted small fw-semibold mb-1">
                    {moduleName}{" "}
                    <span className="fw-normal">
                      ({modulePermissions.length})
                    </span>
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {modulePermissions.map(permission => (
                      <Badge
                        key={permission.id}
                        color="light"
                        className="text-dark fw-normal"
                      >
                        {permission.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Collapse>
        ) : (
          <div className="mt-2">
            <small className="text-muted">No permissions</small>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

const RolesPanel = ({ roles = [] }) => {
  if (!roles.length) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="mdi mdi-shield-off-outline display-4 opacity-50" />
        <p className="mt-2">No roles assigned to this user</p>
      </div>
    )
  }

  return (
    <div className="d-flex flex-column gap-3">
      {roles.map(role => (
        <RoleCard key={role.id} role={role} />
      ))}
    </div>
  )
}

const UserProfile = () => {
  const { identifier } = useParams()
  const { data: response, isLoading } = useUser(identifier)
  const user = useMemo(() => response?.data ?? null, [response])

  const { hasAnyPermission, user: currentUser } = useAuth()
  const canViewActivity = hasAnyPermission([ACCESS.ACTIVITY_LOG.VIEW_LIST])
  const isOwnProfile =
    !!currentUser && !!user && currentUser.id === user.id

  const [params, updateParams] = useUrlParams({ tab: "account" })
  const allowedTabs = useMemo(
    () =>
      canViewActivity
        ? [...USER_PROFILE_BASE_TABS, "activity"]
        : USER_PROFILE_BASE_TABS,
    [canViewActivity]
  )
  const activeTab = allowedTabs.includes(params.tab) ? params.tab : "account"

  const toggleTab = tab => {
    if (activeTab !== tab) {
      updateParams({ tab })
    }
  }

  const statusMeta = user?.is_system
    ? { label: "System", color: "dark" }
    : { label: "Active", color: "success" }

  document.title = `User Profile${user?.name ? ` | ${user.name}` : ""}`

  const accountData = [
    { label: "Full Name", value: user?.name },
    { label: "Username", value: user?.username },
    { label: "Email", value: user?.email },
    {
      label: "Account Type",
      value: user?.is_system ? "System User" : "Standard User",
    },
    {
      label: "Created",
      value: user?.created_at ? formatReadableDate(user.created_at) : "-",
    },
    {
      label: "Last Updated",
      value: user?.updated_at ? formatReadableDate(user.updated_at) : "-",
    },
  ]

  const assignmentData = [
    {
      label: "Showroom",
      value: user?.showroom?.short_name
        ? `${user.showroom.name} (${user.showroom.short_name})`
        : user?.showroom?.name,
    },
    {
      label: "Linked Employee",
      value: user?.employee?.employee_id
        ? `${user.employee.name} (#${user.employee.employee_id})`
        : user?.employee?.name,
    },
    { label: "Designation", value: user?.employee?.designation?.title },
    { label: "Department", value: user?.employee?.department?.name },
  ]

  const tabs = [
    { id: "account", label: "Account", icon: "bx bx-user" },
    { id: "roles", label: "Roles & Permissions", icon: "bx bx-shield" },
    ...(canViewActivity
      ? [{ id: "activity", label: "Activity Log", icon: "bx bx-history" }]
      : []),
  ]

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="User" breadcrumbItem="Profile" loading={isLoading} />

        {!isLoading && !user ? (
          <Card className="border-0 shadow-sm">
            <CardBody className="text-center py-5 text-muted">
              <i className="mdi mdi-account-alert-outline display-4 opacity-50" />
              <p className="mt-2 mb-0">
                User not found with identifier: {identifier}
              </p>
            </CardBody>
          </Card>
        ) : (
          <Row>
            <Col lg="4" className="mb-4">
              <UserProfileCard
                user={user}
                statusMeta={statusMeta}
                loading={isLoading}
                showImageUploader={isOwnProfile}
              />
            </Col>

            <Col lg="8">
              <Nav pills className="nav-pills-custom mb-4">
                {tabs.map(tab => (
                  <NavItem key={tab.id}>
                    <NavLink
                      className={classnames({ active: activeTab === tab.id })}
                      onClick={() => toggleTab(tab.id)}
                    >
                      <i className={`${tab.icon} me-2`}></i>
                      {tab.label}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <TabContent activeTab={activeTab}>
                <TabPane tabId="account">
                  <Row>
                    <Col md="6" className="mb-4">
                      <DataCard
                        title="Account Details"
                        icon="bx bx-id-card"
                        data={accountData}
                      />
                    </Col>
                    <Col md="6" className="mb-4">
                      <DataCard
                        title="Assignment"
                        icon="bx bx-buildings"
                        data={assignmentData}
                      />
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabId="roles">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-4">
                      <SectionHeader
                        icon="bx bx-shield"
                        title="Roles & Permissions"
                      />
                      <RolesPanel roles={user?.roles} />
                    </CardBody>
                  </Card>
                </TabPane>

                {canViewActivity && (
                  <TabPane tabId="activity">
                    <Card className="border-0 shadow-sm">
                      <CardBody className="p-4">
                        <CanView access={[ACCESS.ACTIVITY_LOG.VIEW_LIST]}>
                          <ActivityList user={user} />
                        </CanView>
                      </CardBody>
                    </Card>
                  </TabPane>
                )}
              </TabContent>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  )
}

export default UserProfile
